/**
 * What each exercise *is* to the person looking at it: a code, a family, and a
 * difficulty (GAME-TYPES.md §1 and §2).
 *
 * A teacher building a board picks from thirty-odd Icelandic labels, several of
 * which begin with "Fallbeyging" or "Forsetningar", and a student meets the
 * same exercise in `/leika` and again on a tile. Neither can tell one from
 * another by label alone, so every exercise carries three redundant signals:
 *
 * - the **code** identifies it, and is a complete identifier on its own;
 * - the **family** groups it, and is what the icon and the hue draw;
 * - the **difficulty** describes it, and never touches movement (§2.3).
 *
 * **Why a table here rather than a field on `ExerciseModule`.** Same reason
 * `practice-controls.ts` sits beside it: the §12 contract is deliberately small
 * and was frozen on wave 1, and a round generator has no use for a colour. The
 * cost of keeping them apart is drift, so `identity.test.ts` checks this table
 * against the registry in both directions -- exactly as §4.3 asks.
 *
 * The colours themselves are *not* here. They are CSS custom properties in
 * `apps/web/src/styles/families.css`, because they have to change with the
 * viewer's theme and a constant in a shared package cannot.
 */

export type Family =
  | 'pronouns'
  | 'numbers-time'
  | 'prepositions'
  | 'verbs'
  | 'sound'
  | 'nouns'
  | 'syntax'
  | 'adjectives';

/** Every family, in the hue order of §1.3. Drives legends and swatch rows. */
export const FAMILIES: readonly Family[] = [
  'pronouns',
  'numbers-time',
  'prepositions',
  'verbs',
  'sound',
  'nouns',
  'syntax',
  'adjectives',
];

/** What a family is called in front of a teacher. */
export const FAMILY_LABELS: Readonly<Record<Family, string>> = {
  pronouns: 'Fornöfn',
  'numbers-time': 'Tölur og tími',
  prepositions: 'Forsetningar og fallstjórn',
  verbs: 'Sagnorð',
  sound: 'Hljóð og framburður',
  nouns: 'Nafnorð',
  syntax: 'Setningaskipan',
  adjectives: 'Lýsingarorð',
};

/** 1-5, of the *default* configuration. Several options move it (§2.4). */
export type Difficulty = 1 | 2 | 3 | 4 | 5;

/** The Icelandic weight names shown beside the pips, so they never stand alone. */
export const DIFFICULTY_LABELS: Readonly<Record<Difficulty, string>> = {
  1: 'Mjög létt',
  2: 'Létt',
  3: 'Miðlungs',
  4: 'Þungt',
  5: 'Mjög þungt',
};

export interface ExerciseIdentity {
  /**
   * The Icelandic grammar abbreviation, 2-6 characters, unique across them all.
   *
   * This is the channel that survives everything: a washed-out projector, a
   * greyscale print, deuteranopia. Colour and icon are there to make it
   * findable at a glance, never to replace it.
   */
  code: string;
  family: Family;
  difficulty: Difficulty;
}

/**
 * Keyed by catalog id, ordered by course level so the table reads like the
 * syllabus does.
 */
export const IDENTITY: Readonly<Record<string, ExerciseIdentity>> = {
  // Íslenska 1
  framburdur: { code: 'HLJ', family: 'sound', difficulty: 1 },
  akvedinn_greinir: { code: 'GR', family: 'nouns', difficulty: 2 },
  present: { code: 'NT', family: 'verbs', difficulty: 3 },
  accusative: { code: 'ÞF', family: 'nouns', difficulty: 2 },
  numbers: { code: 'TÖL', family: 'numbers-time', difficulty: 1 },
  numbers_large: { code: 'TÖLh', family: 'numbers-time', difficulty: 3 },
  time: { code: 'KL', family: 'numbers-time', difficulty: 3 },
  adj_gender_singular: { code: 'LO', family: 'adjectives', difficulty: 2 },

  // Íslenska 2
  wordorder: { code: 'ORÐ', family: 'syntax', difficulty: 4 },
  plural: { code: 'FT', family: 'nouns', difficulty: 2 },
  plural_article: { code: 'FTgr', family: 'nouns', difficulty: 3 },
  dates: { code: 'DAG', family: 'numbers-time', difficulty: 2 },
  years: { code: 'ÁRT', family: 'numbers-time', difficulty: 3 },
  forsetningar: { code: 'FS', family: 'prepositions', difficulty: 2 },
  bodhattur: { code: 'BH', family: 'verbs', difficulty: 2 },
  adj_noun: { code: 'TLN', family: 'adjectives', difficulty: 2 },

  // Íslenska 3
  past: { code: 'ÞT', family: 'verbs', difficulty: 4 },
  tholfall_thagufall: { code: 'ÞF/ÞGF', family: 'prepositions', difficulty: 4 },
  sagnbot: { code: 'SB', family: 'verbs', difficulty: 2 },
  cases_singular: { code: 'FALL', family: 'nouns', difficulty: 3 },
  prepositions_cases: { code: 'FS·F', family: 'prepositions', difficulty: 3 },

  // Íslenska 4
  eignarfornofn: { code: 'EIG', family: 'pronouns', difficulty: 4 },
  afturbeygd_fornofn: { code: 'AFT', family: 'pronouns', difficulty: 3 },
  stigbreyting: { code: 'STIG', family: 'adjectives', difficulty: 3 },
  adj_weak_singular: { code: 'LOvk', family: 'adjectives', difficulty: 4 },

  // Íslenska 5
  cases_singular_article: { code: 'FALLgr', family: 'nouns', difficulty: 4 },
  cases_plural: { code: 'FALLft', family: 'nouns', difficulty: 4 },
  cases_plural_article: { code: 'FLftgr', family: 'nouns', difficulty: 4 },
  prepositions_cases_article: { code: 'FS·Fgr', family: 'prepositions', difficulty: 4 },
  subjunctive_vera: { code: 'VH·V', family: 'verbs', difficulty: 3 },
  sinn_hans: { code: 'SINN', family: 'pronouns', difficulty: 4 },

  // Íslenska 6
  time_cases: { code: 'TÍM', family: 'numbers-time', difficulty: 4 },
  cases_adj_noun: { code: 'LO+NO', family: 'adjectives', difficulty: 5 },
  adj_strong_singular: { code: 'LOst', family: 'adjectives', difficulty: 4 },
  adj_strong_plural: { code: 'LOstft', family: 'adjectives', difficulty: 4 },
  subjunctive: { code: 'VH', family: 'verbs', difficulty: 4 },
  midmynd: { code: 'MM', family: 'verbs', difficulty: 5 },
};

/**
 * The identity of an exercise, or null for an id the catalog does not know.
 *
 * Null rather than a throw: a board tile can outlive a catalog re-seed, and a
 * teacher's screen should degrade to the plain label rather than blank out.
 */
export function identityOf(exerciseId: string): ExerciseIdentity | null {
  return Object.hasOwn(IDENTITY, exerciseId) ? (IDENTITY[exerciseId] ?? null) : null;
}
